import clsx from 'clsx';
import { saveAs } from 'file-saver';
import * as React from 'react';
import { ChangeEvent, useState } from 'react';
import useDraft from '../hooks/useDraft';
import { useEditor } from '../hooks/useEditor';
import useKites from '../hooks/useKites';
import FolderIcon from '../icons/FolderIcon';
import { Kite } from '../types';

const recognitionTag = 'mwd-kite-colorizer';

export interface ImportPayload {
    tag: typeof recognitionTag;
    payload: Kite[];
}

export default function ImportExportPage() {
    const { draft } = useDraft();
    const { kites, importKites } = useKites();
    const {
        editor: { draftSaved },
    } = useEditor();

    const [tab, setTab] = useState<number>(0);
    const [exportContext, setExportContext] = useState<'draft' | 'selection'>(
        'selection'
    );
    const [exportName, setExportName] = useState('');

    function handleExport() {
        const importPayload: ImportPayload = {
            tag: recognitionTag,
            payload:
                exportContext === 'draft'
                    ? [draft]
                    : kiteSelection.map((id) => kites[id]),
        };
        const blob = new Blob([JSON.stringify(importPayload)], {
            type: 'text/json;charset=utf-8',
        });

        const fileName = exportName
            ? exportName
            : exportContext === 'draft'
            ? draft.name
            : 'mkc-kite-selection';

        saveAs(
            blob,
            `${fileName.replace(/\s+?/g, '-')}__${new Date().getTime()}.json`
        );
    }
    const [kiteSelection, setKiteSelection] = useState<string[]>([]);
    function handleKiteSelection(id: string) {
        if (!kiteSelection.includes(id)) {
            setKiteSelection([...kiteSelection, id]);
            return;
        }

        const nextSelection = [...kiteSelection];
        nextSelection.splice(nextSelection.indexOf(id), 1);
        setKiteSelection(nextSelection);
    }

    const [importPayload, setImportPayload] = useState<Kite[]>([]);
    const [importError, setImportError] = useState<null | string>(null);

    const handleFileData = (file: File): Promise<ImportPayload> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (eventReader: any) => {
                resolve(JSON.parse(eventReader.target.result));
            };
            reader.readAsText(file);
        });
    };

    function handleChooseFiles(event: ChangeEvent<HTMLInputElement>) {
        setImportError(null);
        Array.from(event.target.files).forEach((file) => {
            handleFileData(file).then((json) => {
                if (!json.tag || json.tag !== recognitionTag || !json.payload) {
                    setImportError(
                        'This appears not to be a valid file. Was it exported via this software?'
                    );
                    return;
                }
                setImportPayload([...json.payload]);
            });
        });
    }
    function handleImport() {
        importKites(importPayload);
        setImportPayload([]);
    }
    function handleRemoveFile(id: string) {
        setImportPayload((paylaod) =>
            paylaod.reduce(
                (nextPayload, kite) =>
                    kite.id === id ? nextPayload : [...nextPayload, kite],
                []
            )
        );
    }

    return (
        <div className="import-export">
            <div className="import-export__tab-controls buttongroup">
                <button
                    className={clsx('button', {
                        isActive: tab === 0,
                    })}
                    onClick={() => setTab(0)}
                >
                    export
                </button>
                <button
                    className={clsx('button', {
                        isActive: tab === 1,
                    })}
                    onClick={() => setTab(1)}
                >
                    import
                </button>
            </div>

            {/* export */}
            {tab === 0 && (
                <>
                    <div className="buttongroup">
                        <button
                            className={clsx('button button--small', {
                                isSelected: exportContext === 'selection',
                            })}
                            onClick={() => setExportContext('selection')}
                        >
                            kite selection
                        </button>
                        <button
                            className={clsx('button button--small', {
                                isSelected: exportContext === 'draft',
                            })}
                            onClick={() => setExportContext('draft')}
                        >
                            draft
                        </button>
                    </div>

                    <div className="import-export-dialog">
                        <input
                            type="text"
                            placeholder="enter export name (optional)"
                            value={exportName}
                            onChange={({ target: { value } }) =>
                                setExportName(value)
                            }
                        />
                    </div>
                    <div className="import-export-dialog">
                        {exportContext === 'draft' ? (
                            <span>{`the current draft "${
                                draft.name
                            }" will be exported${
                                !draftSaved
                                    ? ' (including unsaved changes!)'
                                    : ''
                            }.`}</span>
                        ) : (
                            <span>
                                {kiteSelection.length > 0
                                    ? `${kiteSelection.length} ${
                                          kiteSelection.length > 1
                                              ? 'kites'
                                              : 'kite'
                                      } will be exported`
                                    : 'no kites selected for export'}
                            </span>
                        )}

                        <button
                            className={clsx('button ok')}
                            onClick={handleExport}
                            disabled={
                                exportContext === 'selection' &&
                                kiteSelection.length === 0
                            }
                        >
                            export
                        </button>
                    </div>

                    {exportContext === 'selection' && (
                        <div className={'kite-selection-list'}>
                            {Object.values(kites).map(({ id, name, meta }) => (
                                <dl
                                    key={id}
                                    className={clsx({
                                        isActive: kiteSelection.includes(id),
                                    })}
                                    onClick={() => handleKiteSelection(id)}
                                >
                                    <dt>{name}</dt>
                                    <dd>
                                        {meta.notes || 'no notes available'}
                                    </dd>
                                </dl>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* import */}
            {tab === 1 && (
                <>
                    <div className="import-export-dialog">
                        <span className="import-export-dialog__file">
                            <label htmlFor="mkc-select-import">
                                <FolderIcon />
                                choose files
                            </label>
                            <input
                                key={JSON.stringify(
                                    importPayload.map((kite) => kite.id)
                                )}
                                id={'mkc-select-import'}
                                type="file"
                                accept={'.json'}
                                onInput={handleChooseFiles}
                                // multiple
                            />
                        </span>
                        <button
                            className={clsx('button ok')}
                            onClick={handleImport}
                            disabled={!importPayload || !importPayload.length}
                        >
                            import
                        </button>
                    </div>
                    <div className="import-export-dialog__file-list">
                        {importError ? (
                            <p className={'error'}>{importError}</p>
                        ) : importPayload.length ? (
                            <ul>
                                {importPayload.map((kite) => (
                                    <li key={kite.id}>
                                        <span>{kite.name}</span>
                                        <button
                                            className="button button--small warning"
                                            onClick={() =>
                                                handleRemoveFile(kite.id)
                                            }
                                        >
                                            remove
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            'nothing was uploaded for import'
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
